import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getResult, unlockAttempt, flagQuestionError, generateExplanation, retireQuestion, unretireQuestion } from '../../api';

function toHtml(s) { const t = s || ''; return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>'); }
function HtmlDiv(p) { return React.createElement('div', { style: p.style, dangerouslySetInnerHTML: { __html: toHtml(p.html) } }); }
function HtmlSpan(p) { return React.createElement('span', { style: p.style, dangerouslySetInnerHTML: { __html: toHtml(p.html) } }); }

const OPTS = ['A', 'B', 'C', 'D'];

function statusFor(q) {
  if (q.is_correct) return { color: '#10B981', bg: '#D1FAE5', label: 'Correct', tag: 'GREEN' };
  if (q.review_unlocked) {
    if (q.review_unlock_attempts === 1) return { color: '#F59E0B', bg: '#FEF3C7', label: 'Got it (1 attempt)', tag: 'YELLOW' };
    if (q.review_unlock_attempts === 2) return { color: '#F97316', bg: '#FFEDD5', label: 'Got it (2 attempts)', tag: 'ORANGE' };
    return { color: '#EF4444', bg: '#FEE2E2', label: 'Revealed (3 attempts)', tag: 'RED' };
  }
  if (q.is_attempted) return { color: '#94A3B8', bg: '#F1F5F9', label: 'Incorrect — answer locked', tag: 'GREY' };
  return { color: '#94A3B8', bg: '#F1F5F9', label: 'Skipped — answer locked', tag: 'GREY' };
}

function nextFlagLabel(tier) {
  if (!tier || tier === 0) return 'Flag Error — Sonnet review';
  if (tier === 1) return 'Re-flag — Escalate to Opus';
  if (tier === 2) return 'Re-flag — Send to Manual Review';
  if (tier === 3) return 'Pending manual review';
  return 'Flag Error';
}

function CountdownBadge({ targetIso, onExpire }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const target = new Date(targetIso).getTime();
  const rem = Math.max(0, Math.floor((target - now) / 1000));
  useEffect(() => { if (rem === 0 && onExpire) onExpire(); }, [rem, onExpire]);
  const m = Math.floor(rem / 60), s = rem % 60;
  return <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}</span>;
}

function QuestionCard({ q, index, testId, onUpdate }) {
  const [picker, setPicker] = useState(false);
  const [picking, setPicking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [flagState, setFlagState] = useState(null);   // {loading?, lastResult?}
  const [explState, setExplState] = useState(null);   // {loading?, error?}
  const [retired, setRetired] = useState(!!q.is_retired);
  const [retiring, setRetiring] = useState(false);

  const toggleRetire = async () => {
    setRetiring(true);
    try {
      if (retired) { await unretireQuestion(testId, q.question_id); setRetired(false); }
      else { await retireQuestion(testId, q.question_id); setRetired(true); }
    } catch (e) { /* keep prior state on failure */ }
    finally { setRetiring(false); }
  };

  const status = statusFor(q);
  const revealed = q.is_correct || q.review_unlocked;
  const isLockedNow = !revealed && q.review_locked_remaining_seconds && q.review_locked_remaining_seconds > 0;

  const submitUnlock = async (chosen) => {
    setPicking(chosen); setFeedback(null);
    try {
      const r = await unlockAttempt(testId, q.question_id, { chosen_option: chosen });
      const d = r.data;
      onUpdate(q.question_id, {
        review_unlock_attempts: d.attempts, review_unlocked: d.revealed,
        correct_option: d.correct_option, explanation: d.explanation, chatgpt_review: d.chatgpt_review,
        review_locked_until: d.locked_until || null, review_locked_remaining_seconds: d.seconds_remaining || null,
      });
      if (d.correct) setFeedback({ type: 'right', message: 'Correct! Explanation unlocked.' });
      else if (d.revealed) setFeedback({ type: 'reveal', message: 'Two wrong attempts — answer revealed.' });
      else setFeedback({ type: 'wrong', message: `Wrong. Locked for ${Math.floor((d.seconds_remaining||600)/60)} minutes.` });
      setPicker(false);
    } catch (e) {
      const r = e.response;
      if (r && r.status === 423 && r.data && r.data.detail) {
        const det = r.data.detail;
        onUpdate(q.question_id, { review_locked_until: det.locked_until, review_locked_remaining_seconds: det.seconds_remaining });
        setFeedback({ type: 'wrong', message: `Still locked. ${Math.ceil(det.seconds_remaining/60)} min remaining.` });
      } else setFeedback({ type: 'wrong', message: r?.data?.detail || 'Error — try again.' });
    } finally { setPicking(null); }
  };

  const onFlag = async () => {
    const tier = q.review_tier || 0;
    if (tier >= 3) return;
    let msg = 'Send this question for Claude Sonnet review?';
    if (tier === 1) msg = 'Sonnet has already reviewed. Escalate to Claude Opus for senior in-depth review?';
    if (tier === 2) msg = 'Both Sonnet and Opus reviewed. Queue this question for manual admin review?';
    if (!window.confirm(msg)) return;
    setFlagState({ loading: true });
    try {
      const r = await flagQuestionError(q.question_id);
      const d = r.data;
      setFlagState({ loading: false, lastResult: d });
      onUpdate(q.question_id, {
        correct_option: d.correct_option, explanation: d.explanation,
        chatgpt_review: d.chatgpt_review, review_tier: d.review_tier,
        flag_error_count: d.flag_count,
      });
    } catch (e) {
      setFlagState({ loading: false, lastResult: { status: 'error', detail: e.response?.data?.detail || 'Failed' } });
    }
  };

  const onGenerateExplanation = async () => {
    setExplState({ loading: true });
    try {
      const r = await generateExplanation(q.question_id);
      const d = r.data;
      if (d.status === 'generated') {
        onUpdate(q.question_id, { explanation: d.explanation, chatgpt_review: d.chatgpt_review });
        setExplState({ loading: false, done: true });
      } else {
        setExplState({ loading: false, error: d.detail || 'Failed' });
      }
    } catch (e) {
      setExplState({ loading: false, error: e.response?.data?.detail || 'Failed' });
    }
  };

  const correctLetter = q.correct_option;

  return (
    <div style={{ background: 'white', border: `2px solid ${status.color}`, borderRadius: 12, padding: 18, marginBottom: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ background: status.bg, color: status.color, padding: '4px 10px', borderRadius: 8, fontWeight: 700, fontSize: 12 }}>Q{index + 1} · {status.label}</span>
          <span title="Question ID" style={{ background: '#EEF2FF', color: '#4F46E5', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>QID: {q.question_id}</span>
          {q.flag_error_count > 0 && (
            <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>Flagged {q.flag_error_count}×</span>
          )}
          {q.review_tier > 0 && (
            <span style={{ background: q.review_tier === 3 ? '#FEE2E2' : q.review_tier === 2 ? '#EDE9FE' : '#DBEAFE', color: q.review_tier === 3 ? '#991B1B' : q.review_tier === 2 ? '#5B21B6' : '#1E40AF', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
              {q.review_tier === 1 ? 'Sonnet reviewed' : q.review_tier === 2 ? 'Opus reviewed' : 'Awaiting manual review'}
            </span>
          )}
        </div>
        <button onClick={toggleRetire} disabled={retiring}
          title="Retire = you've mastered this. It leaves the active question bank and only appears in Retired Questions Tests. Get it wrong there and it comes back."
          style={{ flexShrink: 0, background: retired ? '#ECFDF5' : 'transparent', color: retired ? '#047857' : '#64748B', border: `2px solid ${retired ? '#6EE7B7' : '#CBD5E1'}`, borderRadius: 8, padding: '6px 12px', cursor: retiring ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {retiring ? 'Saving…' : retired ? 'Retired \u2713 — Unretire' : 'Retire Question'}
        </button>
      </div>

      {q.passage_text && (
        <div style={{ background: '#F8FAFC', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, fontSize: 14, lineHeight: 1.9 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Passage</div>
          <HtmlDiv html={q.passage_text} />
        </div>
      )}

      <HtmlDiv html={q.question_text} style={{ fontSize: 15, lineHeight: 1.7, marginBottom: 14, fontWeight: 500 }} />
      {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 14, border: '1px solid var(--border)' }} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {OPTS.map(opt => {
          const val = q[`option_${opt.toLowerCase()}`];
          const wasStudent = q.student_answer === opt;
          const isCorrectShown = revealed && correctLetter === opt;
          let border = '#E2E8F0', bg = 'white', tint = '#0F172A';
          if (isCorrectShown) { border = '#10B981'; bg = '#D1FAE5'; }
          else if (wasStudent && q.is_attempted && !q.is_correct) { border = '#EF4444'; bg = '#FEE2E2'; }
          else if (wasStudent) { border = '#4F46E5'; bg = '#EEF2FF'; }
          return (
            <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `2px solid ${border}`, background: bg }}>
              <span style={{ width: 26, height: 26, borderRadius: 6, background: border, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{opt}</span>
              <HtmlSpan html={val} style={{ fontSize: 14, color: tint, flex: 1 }} />
              {wasStudent && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#FFF', color: '#475569' }}>Your answer</span>}
              {isCorrectShown && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#10B981', color: 'white' }}>Correct</span>}
            </div>
          );
        })}
      </div>

      {!revealed && (
        <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 12, marginTop: 4 }}>
          {isLockedNow && (
            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400E' }}>
              You answered wrong. Locked for{' '}
              <CountdownBadge targetIso={q.review_locked_until} onExpire={() => onUpdate(q.question_id, { review_locked_remaining_seconds: 0 })} />
              {' '}then try once more. (Attempt {q.review_unlock_attempts}/2)
            </div>
          )}
          {!isLockedNow && !picker && (
            <button onClick={() => setPicker(true)} style={{ background: '#4F46E5', color: 'white', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              {q.review_unlock_attempts > 0 ? `Try again — Attempt ${q.review_unlock_attempts + 1}/2` : 'Unlock correct answer'}
            </button>
          )}
          {!isLockedNow && picker && (
            <div>
              <div style={{ fontSize: 13, marginBottom: 8, color: '#475569' }}>Choose what you now think is the correct answer:</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {OPTS.map(opt => (
                  <button key={opt} disabled={!!picking} onClick={() => submitUnlock(opt)}
                    style={{ background: picking === opt ? '#94A3B8' : 'white', color: '#4F46E5', border: '2px solid #4F46E5', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14, minWidth: 50 }}>{opt}</button>
                ))}
                <button onClick={() => { setPicker(false); setFeedback(null); }} disabled={!!picking}
                  style={{ background: 'transparent', color: '#94A3B8', border: '1px solid #CBD5E1', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}
          {feedback && (
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8,
                          background: feedback.type === 'right' ? '#D1FAE5' : feedback.type === 'reveal' ? '#FEE2E2' : '#FEF3C7',
                          color: feedback.type === 'right' ? '#065F46' : feedback.type === 'reveal' ? '#991B1B' : '#92400E',
                          fontSize: 13, fontWeight: 600 }}>{feedback.message}</div>
          )}
        </div>
      )}

      {revealed && (
        <div style={{ marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Explanation</div>
          {q.explanation ? (
            <HtmlDiv html={q.explanation} style={{ fontSize: 14, lineHeight: 1.7, color: '#0F172A' }} />
          ) : (
            <div style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 8, padding: 12, color: '#64748B', fontSize: 13 }}>
              No explanation available yet.
              {!explState && (
                <button onClick={onGenerateExplanation}
                  style={{ marginLeft: 10, background: '#6366F1', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Get Detailed Explanation
                </button>
              )}
              {explState && explState.loading && <span style={{ marginLeft: 10, color: '#6366F1' }}>Generating from Claude Sonnet (NEET UG + NCERT)…</span>}
              {explState && explState.error && <span style={{ marginLeft: 10, color: '#EF4444' }}>Failed: {explState.error}</span>}
            </div>
          )}
          {q.explanation_image_url && <img src={q.explanation_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 10, border: '1px solid var(--border)' }} />}
          {q.chatgpt_review && (
            <div style={{ marginTop: 12, background: '#F8FAFC', borderLeft: '3px solid #6366F1', padding: '10px 14px', fontSize: 13, color: '#334155' }}>
              <div style={{ fontWeight: 700, color: '#4F46E5', marginBottom: 4, fontSize: 11, textTransform: 'uppercase' }}>Reviewer Audit Log</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 13 }}>{q.chatgpt_review.trim()}</pre>
            </div>
          )}
        </div>
      )}

      {revealed && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {flagState && flagState.loading && (
            <div style={{ fontSize: 12, color: '#64748B' }}>Sending to {q.review_tier === 0 ? 'Claude Sonnet' : q.review_tier === 1 ? 'Claude Opus (deep review)' : 'manual review queue'}…</div>
          )}
          {flagState && !flagState.loading && flagState.lastResult && (
            <div style={{ flex: '1 1 200px', padding: '10px 14px', borderRadius: 8,
                          background: flagState.lastResult.status === 'updated' ? '#FFEDD5'
                                  : flagState.lastResult.status === 'agrees' ? '#D1FAE5'
                                  : flagState.lastResult.status === 'queued_manual' || flagState.lastResult.status === 'already_pending_manual' ? '#FEE2E2'
                                  : '#FEF3C7',
                          color: '#0F172A', fontSize: 13, fontWeight: 500 }}>
              {flagState.lastResult.status === 'agrees' && <><strong>{flagState.lastResult.reviewer}:</strong> Agreed. See audit log above.</>}
              {flagState.lastResult.status === 'updated' && <><strong>{flagState.lastResult.reviewer}:</strong> Updated answer from {flagState.lastResult.previous_correct_option} to {flagState.lastResult.correct_option}.</>}
              {flagState.lastResult.status === 'queued_manual' && <><strong>Manual review queued.</strong> An admin will resolve this; Sonnet/Opus already reviewed.</>}
              {flagState.lastResult.status === 'already_pending_manual' && <><strong>Already pending manual review.</strong></>}
              {flagState.lastResult.status === 'error' && <>Flagged, but review failed: {flagState.lastResult.detail}</>}
            </div>
          )}
          <button onClick={onFlag} disabled={(q.review_tier >= 3) || (flagState && flagState.loading)}
            style={{ background: 'transparent', color: q.review_tier >= 3 ? '#94A3B8' : '#EF4444',
                     border: `1px solid ${q.review_tier >= 3 ? '#CBD5E1' : '#FCA5A5'}`,
                     borderRadius: 6, padding: '6px 12px',
                     cursor: q.review_tier >= 3 ? 'not-allowed' : 'pointer',
                     fontSize: 12, fontWeight: 600 }}>
            {nextFlagLabel(q.review_tier)}
          </button>
        </div>
      )}
    </div>
  );
}

export default function TestResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['result', id], queryFn: () => getResult(id).then(r => r.data) });
  const [questions, setQuestions] = useState([]);
  useEffect(() => { if (data?.all_questions) setQuestions(data.all_questions); }, [data]);

  const handleUpdate = useCallback((qid, patch) => {
    setQuestions(prev => prev.map(q => q.question_id === qid ? { ...q, ...patch } : q));
  }, []);

  if (isLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><div className="spinner" /></div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#EF4444' }}>{error.response?.data?.detail || 'Error loading result'}</div>;
  const pct = data.max_score > 0 ? Math.round((data.score / data.max_score) * 100) : 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 22, marginBottom: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 24, color: '#0F172A' }}>Score: {data.score} / {data.max_score}</div>
            <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>{data.correct} correct · {data.incorrect} incorrect · {data.total_questions - data.attempted} skipped</div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: '#3498DB' }}>{(data.accuracy_pct || 0).toFixed(1)}%</div><div style={{ fontSize: 11, color: '#64748B' }}>Accuracy</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: '#9B59B6' }}>{(data.attempt_pct || 0).toFixed(1)}%</div><div style={{ fontSize: 11, color: '#64748B' }}>Attempt</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 800, color: pct >= 60 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444' }}>{pct}%</div><div style={{ fontSize: 11, color: '#64748B' }}>Score</div></div>
          </div>
        </div>
        <div style={{ marginTop: 14, fontSize: 12, color: '#64748B' }}>
          <strong>Review:</strong> Right questions show the explanation. For wrong/skipped, click Unlock — 2 tries, 10-min lock between. Click <strong>Flag Error</strong> if you think the answer or explanation is wrong: 1st flag → Sonnet, 2nd → Opus deep review, 3rd → human admin review. If no explanation is present, click <strong>Get Detailed Explanation</strong> (NCERT-grounded).
        </div>
      </div>
      {questions.map((q, i) => (<QuestionCard key={q.question_id} q={q} index={i} testId={id} onUpdate={handleUpdate} />))}
      <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
        <button className="btn btn-primary" onClick={() => navigate('/student/test/new')}>Take another test</button>
        <button className="btn btn-ghost" onClick={() => navigate('/student')}>Dashboard</button>
      </div>
    </div>
  );
}
